import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap, withLatestFrom, mergeMap, catchError, map, tap, debounceTime } from 'rxjs';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { select, Action, Store } from '@ngrx/store';
import { ApplicationState } from '../../store/application-store';
import { DefaultDashboardLayoutService } from '../services/default-dashboard-layout.service';
import { DashboardItem } from '../../dashboards/models/dashboard-item';
import { ToastrService } from 'ngx-toastr';
import { GlobalErrorHandler } from '../../common/services/global-error-handler.service';
import * as dashboardDesignerActions from '../actions/dashboard-designer';
import { DashboardHelperService } from '../../common/services/dashboard-helper-service';
import * as dashboardDesignerSelectors from '../selectors/dashboard-designer';

@Injectable()
export class DashboardDesignerEffects {

  constructor(private actions$: Actions,
    private defaultDashboardLayoutService: DefaultDashboardLayoutService,
    private store: Store<ApplicationState>,
    private errorHandler: GlobalErrorHandler,
    private messageService: ToastrService,
    private dashboardHelper: DashboardHelperService
  ) { }

  
  load$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardDesignerActions.Load>(dashboardDesignerActions.LOAD),
    switchMap(action => {
      return this.defaultDashboardLayoutService.get(action.payload.profileId, action.payload.dashboardIdentifier).pipe(
        map((items: DashboardItem[]) => new dashboardDesignerActions.LoadComplete(items)),
        catchError(err => of(new dashboardDesignerActions.LoadError(err)))
      );
    })));

  
  loadError$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardDesignerActions.LoadError>(dashboardDesignerActions.LOAD_ERROR),
    tap((err: any) => {
      this.errorHandler.handleError(err.payload);
    })), { dispatch: false });

  
  addWidget: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardDesignerActions.AddWidget>(dashboardDesignerActions.ADD_WIDGET),
    withLatestFrom(this.store.pipe(select(dashboardDesignerSelectors.selectAllDashboardItems))),
    mergeMap(([action, items]) => {

      const newItem = action.payload.dashboardItem;
      this.dashboardHelper.setNewItemAxis(newItem, items);
      const newItems = [...items, newItem];

      return this.defaultDashboardLayoutService
        .setDashboardItems(action.payload.profileId, action.payload.dashboardIdentifier, newItems).pipe(
          map((items: DashboardItem[]) => new dashboardDesignerActions.LoadComplete(items)),
          catchError(err => {
            return of({ type: 'ADD_ITEM_ERROR' });
          }));
    })));

  
  deleteItem: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardDesignerActions.DeleteWidget>(dashboardDesignerActions.DELETE_WIDGET),
    withLatestFrom(this.store.pipe(select(dashboardDesignerSelectors.selectAllDashboardItems))),
    mergeMap(([action, items]) => {

      const newItems = items.filter(item => item.id !== action.payload.dashboardItem.id);

      return this.defaultDashboardLayoutService
        .setDashboardItems(action.payload.profileId, action.payload.dashboardIdentifier, newItems).pipe(
          map((items: DashboardItem[]) => {
            if (items.length === 0) {
              this.messageService.info('No items left on dashboard, default items will be used.', undefined, { timeOut: 0, extendedTimeOut: 0 });
              return new dashboardDesignerActions.Load(
                new dashboardDesignerActions.LoadArguments(action.payload.profileId, action.payload.dashboardIdentifier));
            }
            return new dashboardDesignerActions.LoadComplete(items);
          }),
          catchError(err => {
            return of({ type: 'DELETE_ITEM_ERROR_UNKNOWN_ACTION' });
          }));
    })));

  
  moveItem: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardDesignerActions.MoveWidget>(dashboardDesignerActions.MOVE_WIDGET),
    debounceTime(1000),
    withLatestFrom(this.store.pipe(select(dashboardDesignerSelectors.selectAllDashboardItems))),
    switchMap(([action, items]) => {
      return this.defaultDashboardLayoutService
        .setDashboardItems(action.payload.profileId, action.payload.dashboardIdentifier, items).pipe(
          map(res => {
            return { type: 'NO_OP_ACTION' };
          }),
          catchError(err => {
            return of({ type: 'MOVE_ITEM_ERROR' });
          }));
    })));

  
  reset: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<dashboardDesignerActions.Reset>(dashboardDesignerActions.RESET),
    debounceTime(1000),
    switchMap(action => {
      return this.defaultDashboardLayoutService
        .setDashboardItems(action.payload.profileId, action.payload.dashboardIdentifier, []).pipe(
          map(res => {
            this.messageService.info('Dashboard Reset Successfully');
            return new dashboardDesignerActions.Load(
              new dashboardDesignerActions.LoadArguments(action.payload.profileId, action.payload.dashboardIdentifier)
            );
          }),
          catchError(err => {
            return of({ type: 'RESET_ERROR' });
          }));
    })));

}
